import type { ReactNode } from "react";
import { cn } from "./cn";
import { BADGE_TONES, type Tone } from "./Badge";

export const ENTITY_CARD_ACCENTS = {
  neutral: { border: "border-line", header: "" },
  primary: { border: "border-primary-500/30", header: "bg-primary-500/5" },
  success: { border: "border-success/30", header: "bg-success/5" },
  warning: { border: "border-warning/30", header: "bg-warning/5" },
  error: { border: "border-error/30", header: "bg-error/5" },
  info: { border: "border-info/30", header: "bg-info/5" },
} as const satisfies Record<Tone, { border: string; header: string }>;

export type EntityCardAccent = keyof typeof ENTITY_CARD_ACCENTS;

export interface EntityCardProps {
  children: ReactNode;
  accent?: EntityCardAccent;
  selected?: boolean;
  className?: string;
  onClick?: () => void;
}

export function EntityCard({
  children,
  accent = "neutral",
  selected = false,
  className,
  onClick,
}: EntityCardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "flex h-full flex-col rounded-2xl border bg-card transition-shadow hover:shadow-custom",
        ENTITY_CARD_ACCENTS[accent].border,
        selected ? "ring-2 ring-primary-500" : undefined,
        onClick ? "cursor-pointer" : undefined,
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface EntityCardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  iconTone?: Tone;
  iconClassName?: string;
  /** El icono ya trae su propio recuadro: no lo envuelvas ni lo tintes. */
  iconBare?: boolean;
  accent?: EntityCardAccent;
  actions?: ReactNode;
  className?: string;
}

export function EntityCardHeader({
  title,
  subtitle,
  icon,
  iconTone = "neutral",
  iconClassName,
  iconBare = false,
  accent = "neutral",
  actions,
  className,
}: EntityCardHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 border-b border-line-soft px-4 pb-3 pt-4 first:rounded-t-2xl",
        ENTITY_CARD_ACCENTS[accent].header,
        className,
      )}
    >
      {icon && iconBare ? icon : null}

      {icon && !iconBare ? (
        <span
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg",
            iconClassName ?? BADGE_TONES[iconTone].soft,
          )}
        >
          {icon}
        </span>
      ) : null}

      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-bold leading-tight text-content">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 truncate text-[11px] text-content-faint">{subtitle}</p>
        ) : null}
      </div>

      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export interface EntityCardBodyProps {
  children: ReactNode;
  className?: string;
}

export function EntityCardBody({ children, className }: EntityCardBodyProps) {
  return (
    <div className={cn("flex-1 space-y-2 px-4 py-3 text-xs", className)}>{children}</div>
  );
}

export interface EntityCardRowProps {
  label: ReactNode;
  children: ReactNode;
  align?: "center" | "start";
  className?: string;
}

export function EntityCardRow({
  label,
  children,
  align = "center",
  className,
}: EntityCardRowProps) {
  return (
    <div
      className={cn(
        "flex justify-between gap-2",
        align === "center" ? "items-center" : "items-start",
        className,
      )}
    >
      <span className="shrink-0 text-content-secondary">{label}</span>
      <span className="ml-2 min-w-0 truncate text-right font-medium text-content">
        {children}
      </span>
    </div>
  );
}

export interface EntityCardActionsProps {
  children: ReactNode;
  className?: string;
}

export function EntityCardActions({ children, className }: EntityCardActionsProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 border-t border-line-soft px-4 py-3 [&>*]:min-w-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface EntityCardFooterProps {
  children: ReactNode;
  className?: string;
}

export function EntityCardFooter({ children, className }: EntityCardFooterProps) {
  return (
    <div
      className={cn(
        "border-t border-line-soft px-4 py-2.5 text-[11px] text-content-faint",
        className,
      )}
    >
      {children}
    </div>
  );
}
